import { Animated, Platform, Pressable, StyleSheet, View } from "react-native";

import { AudienceIcon, CleanAudioIcon } from "@/design/icons";
import { webTouchControl } from "@/design/interactionMotion";
import { Muted } from "@/design/Text";
import {
  hoverRingStrong,
  palette,
  playerGlassBlur,
  radius,
  space
} from "@/design/tokens";

const glassBlur = Platform.OS === "web" && (playerGlassBlur as object);
import type { VideoSurfaceAudioTrack } from "@/playback/players/VideoSurface";
import { usePlayerChromeOpacity } from "@/playback/usePlayerChromeVisibility";

/** Shared heuristic: which of the recital's two feeds a track represents. */
export function isCleanAudioTrack(track: VideoSurfaceAudioTrack) {
  return `${track.id} ${track.label}`.toLowerCase().includes("clean");
}

function shortFeedLabel(track: VideoSurfaceAudioTrack) {
  return isCleanAudioTrack(track) ? "Clean" : "Audience";
}

export type AudioFeedChipProps = {
  tracks: VideoSurfaceAudioTrack[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** More than two tracks: the chip defers to the full settings sheet. */
  onOpenSheet?: () => void;
  compact?: boolean;
  /** Fades with the surrounding chrome (matches HeroCastPill's rhythm). */
  visible?: boolean;
  /** Keep the full player chrome pinned while this overlay is being used. */
  onInteractionStart?: () => void;
  onInteractionEnd?: () => void;
};

/**
 * First-class audience/clean feed switcher — part of the universal chrome
 * grammar, top-left on every player surface. TVs bury (or outright ignore)
 * HLS alternate-audio pickers, and a hero feature never lives in a gear.
 */
export function AudioFeedChip({
  tracks,
  selectedId,
  onSelect,
  onOpenSheet,
  compact = false,
  visible = true,
  onInteractionStart,
  onInteractionEnd
}: AudioFeedChipProps) {
  const opacity = usePlayerChromeOpacity(visible);

  if (tracks.length < 2) return null;

  if (tracks.length > 2) {
    const selected = tracks.find((track) => track.id === selectedId) ?? tracks[0];
    return (
      <Animated.View
        onPointerEnter={onInteractionStart}
        onPointerLeave={onInteractionEnd}
        style={{ opacity, pointerEvents: visible ? "auto" : "none" }}
      >
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={`Audio: ${selected.label}. Change audio track`}
        onBlur={onInteractionEnd}
        onFocus={onInteractionStart}
        onPress={onOpenSheet}
        style={({ hovered, pressed }) => [
          styles.shell,
          webTouchControl,
          glassBlur,
          hovered && styles.shellHovered,
          pressed && { opacity: 0.72 }
        ]}
      >
        <View style={styles.segmentContent}>
          {isCleanAudioTrack(selected) ? (
            <CleanAudioIcon size={15} color={palette.text} />
          ) : (
            <AudienceIcon size={15} color={palette.text} />
          )}
          <Muted variant="smallStrong" color={palette.text} numberOfLines={1}>
            {selected.label}
          </Muted>
        </View>
      </Pressable>
      </Animated.View>
    );
  }

  if (compact) {
    const selectedIndex = Math.max(
      0,
      tracks.findIndex((track) => track.id === selectedId)
    );
    const selected = tracks[selectedIndex] ?? tracks[0];
    const next = tracks[(selectedIndex + 1) % tracks.length] ?? tracks[0];

    return (
      <Animated.View
        onPointerEnter={onInteractionStart}
        onPointerLeave={onInteractionEnd}
        style={{ opacity, pointerEvents: visible ? "auto" : "none" }}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`${selected.label} audio. Switch to ${next.label}`}
          onBlur={onInteractionEnd}
          onFocus={onInteractionStart}
          onPress={() => onSelect(next.id)}
          style={({ hovered, pressed }) => [
            styles.shell,
            webTouchControl,
            glassBlur,
            hovered && styles.shellHovered,
            pressed && { opacity: 0.72 }
          ]}
        >
          {tracks.map((track) => {
            const isSelected = track.id === selected.id;
            const Icon = isCleanAudioTrack(track) ? CleanAudioIcon : AudienceIcon;
            return (
              <View
                key={track.id}
                style={[
                  styles.segment,
                  styles.segmentCompact,
                  isSelected && styles.segmentSelected
                ]}
              >
                <View style={styles.segmentContent}>
                  <Icon
                    size={16}
                    color={isSelected ? palette.text : palette.muted}
                  />
                </View>
              </View>
            );
          })}
        </Pressable>
      </Animated.View>
    );
  }

  return (
    <Animated.View
      accessibilityRole="radiogroup"
      accessibilityLabel="Audio feed"
      onPointerEnter={onInteractionStart}
      onPointerLeave={onInteractionEnd}
      style={[styles.shell, glassBlur, { opacity, pointerEvents: visible ? "auto" : "none" }]}
    >
      {tracks.map((track) => {
        const selected = track.id === selectedId;
        const Icon = isCleanAudioTrack(track) ? CleanAudioIcon : AudienceIcon;
        return (
          <Pressable
            key={track.id}
            accessibilityRole="radio"
            accessibilityState={{ selected }}
            accessibilityLabel={track.label}
            onBlur={onInteractionEnd}
            onFocus={onInteractionStart}
            onPress={() => onSelect(track.id)}
            style={({ hovered, pressed }) => [
              styles.segment,
              webTouchControl,
              selected && styles.segmentSelected,
              !selected && hovered && styles.segmentHovered,
              pressed && { opacity: 0.72 }
            ]}
          >
            <View style={styles.segmentContent}>
              <Icon
                size={15}
                color={selected ? palette.text : palette.muted}
              />
              <Muted
                variant="smallStrong"
                color={selected ? palette.text : palette.textSoft}
              >
                {shortFeedLabel(track)}
              </Muted>
            </View>
          </Pressable>
        );
      })}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  // Same shell language as RemotePlaybackRouteChip: dark glass, cool
  // hairline, pill silhouette — the Remote's two corners match.
  shell: {
    alignItems: "center",
    backgroundColor: palette.playerGlass,
    borderColor: palette.hairlineStrong,
    borderRadius: radius.pill,
    borderWidth: 1,
    flexDirection: "row",
    // The gap matches the shell padding, so the lit segment floats inside
    // the track instead of fusing with its neighbor.
    gap: 3,
    minHeight: 40,
    padding: 3
  },
  shellHovered: {
    backgroundColor: palette.playerGlassHover,
    ...hoverRingStrong
  },
  segment: {
    alignItems: "center",
    borderRadius: radius.pill,
    justifyContent: "center",
    minHeight: 32,
    paddingHorizontal: space.md
  },
  segmentCompact: {
    minWidth: 40,
    paddingHorizontal: space.sm
  },
  segmentSelected: {
    backgroundColor: "rgba(167, 178, 216, 0.18)"
  },
  segmentHovered: {
    backgroundColor: "rgba(167, 178, 216, 0.08)"
  },
  segmentContent: {
    alignItems: "center",
    flexDirection: "row",
    gap: 6
  }
});
